import { fetchUtils, withLifecycleCallbacks } from 'react-admin'
import { stringify } from 'query-string'
import { apiUrl } from './config/constants'

const upperFirst = require('lodash.upperfirst')

const httpClient = fetchUtils.fetchJson

export const inviteUser = async (params) => {
  const endpointId = 'inviteUser'
  const url = `${apiUrl}/${endpointId}`

  const headers = buildAuthHeaders()

  return httpClient(url, {
    method: 'POST',
    body: JSON.stringify(params),
    headers,
  }).then(({ json }) => ({ data: json }))
}

export const registerUser = async (params) => {
  const endpointId = 'registerUser'
  const url = `${apiUrl}/${endpointId}`

  const headers = buildAuthHeaders()

  return httpClient(url, {
    method: 'POST',
    body: JSON.stringify(params),
    headers,
  }).then(({ json }) => ({ data: json }))
}

const buildAuthHeaders = () => {
  const token = localStorage.getItem('auth')
  const headers = new Headers({ Accept: 'application/json', auth: token })

  return headers
}

const getSingular = (plural) => {
  const last3 = plural.slice(-3)
  if (last3 === 'ies') {
    const coreSignular = plural.slice(0, -3)

    return `${coreSignular}y`
  }

  return plural.slice(0, -1)
}

const _buildMyScope = (params) => {
  let myScope = ''

  const meta = params.meta || {}
  if (meta.isMy) {
    myScope = 'My'
  }

  return myScope
}

export const baseDataProvider = {
  getList: (resource, params) => {
    const myScope = _buildMyScope(params)

    const endpointId = `index${myScope}${upperFirst(resource)}`
    const { page, perPage } = params.pagination
    const { field, order } = params.sort

    let sortOrder = order || 'DESC'
    let sortBy = field || 'createdAt'
    if (sortBy === 'id') {
      sortBy = 'createdAt'
      sortOrder = 'DESC'
    }

    const skip = (page - 1) * perPage
    const query = {
      sortOrder,
      sortBy,
      skip,
      limit: perPage,
    }

    const { filter } = params

    if (filter && Object.keys(filter).length > 0) {
      for (const filterField in filter) {
        const value = filter[filterField]

        query[filterField] = value
      }
    }

    const url = `${apiUrl}/${endpointId}?${stringify(query)}`

    const headers = buildAuthHeaders()

    return httpClient(url, { headers }).then(({ headers, json }) => ({
      data: json,
      total: Number(headers.get('x-total-count')) || json.length,
    }))
  },

  getOne: (resource, params) => {
    const myScope = _buildMyScope(params)

    const modelName = getSingular(resource)
    const endpointId = `read${myScope}${upperFirst(modelName)}`
    const url = `${apiUrl}/${endpointId}?id=${params.id}`

    const headers = buildAuthHeaders()

    return httpClient(url, { headers }).then(({ json }) => ({
      data: json,
    }))
  },

  getMany: (resource, params) => {
    const myScope = _buildMyScope(params)

    const endpointId = `index${myScope}${upperFirst(resource)}`
    const { ids } = params
    // const query = {
    //   filter: JSON.stringify({ id: params.ids }),
    // }
    // const url = `${apiUrl}/${endpointId}?${stringify(query)}`
    const headers = buildAuthHeaders()
    const url = `${apiUrl}/${endpointId}?ids=${ids}`
    return httpClient(url, { headers }).then(({ json }) => ({ data: json }))
  },

  getManyReference: (resource, params) => {
    const myScope = _buildMyScope(params)

    const endpointId = `index${myScope}${upperFirst(resource)}`
    const { page, perPage } = params.pagination
    const { field, order } = params.sort
    const query = {
      sort: JSON.stringify([field, order]),
      range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
      filter: JSON.stringify({
        ...params.filter,
        [params.target]: params.id,
      }),
    }
    const url = `${apiUrl}/${endpointId}?${stringify(query)}`

    const headers = buildAuthHeaders()

    return httpClient(url, { headers }).then(({ headers, json }) => ({
      data: json,
      total: parseInt(headers.get('content-range').split('/').pop(), 10),
    }))
  },

  update: (resource, params) => {
    const myScope = _buildMyScope(params)

    const modelName = getSingular(resource)
    const endpointId = `update${myScope}${upperFirst(modelName)}`

    const headers = buildAuthHeaders()

    return httpClient(`${apiUrl}/${endpointId}?id=${params.id}`, {
      headers,
      method: 'PUT',
      body: JSON.stringify(params.data),
    }).then(({ json }) => ({ data: json }))
  },

  updateMany: (resource, params) => {
    const query = {
      filter: JSON.stringify({ id: params.ids }),
    }
    return httpClient(`${apiUrl}/${resource}?${stringify(query)}`, {
      method: 'PUT',
      body: JSON.stringify(params.data),
    }).then(({ json }) => ({ data: json }))
  },

  create: (resource, params) => {
    const myScope = _buildMyScope(params)

    const headers = buildAuthHeaders()
    const modelName = getSingular(resource)
    const endpointId = `create${myScope}${upperFirst(modelName)}`
    return httpClient(`${apiUrl}/${endpointId}`, {
      headers,
      method: 'POST',
      body: JSON.stringify(params.data),
    }).then(({ json }) => ({
      data: { ...params.data, id: json.id },
    }))
  },

  delete: (resource, params) => {
    const myScope = _buildMyScope(params)

    const headers = buildAuthHeaders()
    const modelName = getSingular(resource)
    let endpointId = `delete${myScope}${upperFirst(modelName)}`

    return httpClient(`${apiUrl}/${endpointId}?id=${params.id}`, {
      headers,
      method: 'DELETE',
    }).then(({ json }) => ({ data: json }))
  },

  deleteMany: (resource, params) => {
    const query = {
      filter: JSON.stringify({ id: params.ids }),
    }
    return httpClient(`${apiUrl}/${resource}?${stringify(query)}`, {
      method: 'DELETE',
    }).then(({ json }) => ({ data: json }))
  },

  getUserProfile() {
    const profile = localStorage.getItem('userProfile')

    if (!profile) {
      return Promise.resolve({ data: {} })
    }
    const data = JSON.parse(profile)
    return Promise.resolve({ data })
  },

  async updateUserProfile({ data }) {
    // Convert a newly uploaded file to b64
    const avatar = await (data.avatar.rawFile instanceof File
      ? convertFileToBase64(data.avatar)
      : data.avatar)

    localStorage.setItem(
      'userProfile',
      JSON.stringify({
        ...data,
        id: 'unique_id',
        avatar,
      })
    )
    return Promise.resolve({ data })
  },
}

const removeNullValues = (value) => {
  if (Array.isArray(value)) {
    return value.filter((item) => item != null).map(removeNullValues)
  }
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, v]) => v != null)
        .map(([k, v]) => [k, removeNullValues(v)])
    )
  }
  return value
}

export const dataProvider = withLifecycleCallbacks(baseDataProvider, [
  {
    resource: 'questions',
    beforeCreate: async (params) => {
      if (params.data.image) {
        const formData = new FormData()
        formData.append('file', params.data.image.rawFile)
        const headers = buildAuthHeaders()
        const middlewareId = 'media/questions'
        const { json } = await httpClient(`${apiUrl}/${middlewareId}`, {
          headers,
          method: 'POST',
          body: formData,
        })
        const { url } = json
        params.data.imageUrl = url
        delete params.data.image
      }
      params.data = removeNullValues(params.data)
      return params
    },
    beforeUpdate: async (params) => {
      if (params.data.image) {
        const formData = new FormData()
        formData.append('file', params.data.image.rawFile)
        const headers = buildAuthHeaders()
        const middlewareId = 'media/questions'
        const { json } = await httpClient(`${apiUrl}/${middlewareId}`, {
          headers,
          method: 'POST',
          body: formData,
        })
        const { url } = json
        params.data.imageUrl = url
        delete params.data.image
      }
      params.data = removeNullValues(params.data)
      return params
    },
  },
  // {
  //   resource: 'products',
  //   beforeCreate: async (params, dataProvider) => {
  //     if (params.data.image) {
  //       const formData = new FormData()
  //       formData.append('file', params.data.image.rawFile)
  //       const headers = buildAuthHeaders()
  //       const middlewareId = 'media/products'
  //       const { json } = await httpClient(`${apiUrl}/${middlewareId}`, {
  //         headers,
  //         method: 'POST',
  //         body: formData,
  //       })
  //       const { url } = json
  //       params.data.imageUrl = url
  //       delete params.data.image
  //     }
  //     return params
  //   }
  // }
  {
    resource: 'sections',
    beforeCreate: async (params, dataProvider) => {
      if (Array.isArray(params.data.imageUrls)) {
        const headers = buildAuthHeaders()
        const middlewareId = 'media/courses'

        params.data.imageUrls = await Promise.all(
          params.data.imageUrls.map(async (image) => {
            if (!(image && image.rawFile instanceof File)) {
              return image
            }

            const formData = new FormData()
            formData.append('file', image.rawFile)
            const { json } = await httpClient(`${apiUrl}/${middlewareId}`, {
              headers,
              method: 'POST',
              body: formData,
            })
            console.log('sections - beforeCreate - json', json)
            return json.url
          })
        )
      }
      return params
    },
    beforeUpdate: async (params, dataProvider) => {
      if (Array.isArray(params.data.imageUrls)) {
        const headers = buildAuthHeaders()
        const middlewareId = 'media/courses'

        params.data.imageUrls = await Promise.all(
          params.data.imageUrls.map(async (image) => {
            if (!(image && image.rawFile instanceof File)) {
              return image?.src || image
            }

            const formData = new FormData()
            formData.append('file', image.rawFile)
            const { json } = await httpClient(`${apiUrl}/${middlewareId}`, {
              headers,
              method: 'POST',
              body: formData,
            })
            console.log('sections - beforeCreate - json', json)
            return json.url
          })
        )
      }
      return params
    },
  },
  {
    resource: 'courses',
    beforeCreate: async (params, dataProvider) => {
      if (params.data.image) {
        const formData = new FormData()
        formData.append('file', params.data.image.rawFile)
        const headers = buildAuthHeaders()
        const middlewareId = 'media/courses'
        const { json } = await httpClient(`${apiUrl}/${middlewareId}`, {
          headers,
          method: 'POST',
          body: formData,
        })
        const { url } = json
        params.data.imageUrl = url
        delete params.data.image
      }
      return params
    },
    beforeUpdate: async (params, dataProvider) => {
      if (params.data.image) {
        const formData = new FormData()
        formData.append('file', params.data.image.rawFile)
        const headers = buildAuthHeaders()
        const middlewareId = 'media/courses'
        const { json } = await httpClient(`${apiUrl}/${middlewareId}`, {
          headers,
          method: 'POST',
          body: formData,
        })
        const { url } = json
        params.data.imageUrl = url
        delete params.data.image
      }
      return params
    },
  },
])

const convertFileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject

    reader.readAsDataURL(file.rawFile)
  })
